import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart } from "./LineChart";
import { useState, useEffect, useMemo, useRef } from "react";
import { AlertCircle, ImageIcon, Loader2, MapPin, Search, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useMunicipalityContext } from "@/context/MunicipalityContext";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import { fetchConnectAnalytics, fetchConnectAnalyticsComplaints, fetchConnectAnalyticsQuestion, fetchConnectAnalyticsQuestions } from "@/api/connect_analytics";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import remarkGfm from "remark-gfm";
import "@github/relative-time-element";

type QuestionsPanelScope =
  | { kind: "all" }
  | { kind: "topic"; topic: string };

type ConnectAnalyticsData = Awaited<ReturnType<typeof fetchConnectAnalytics>>;
type ConnectAnalyticsQuestion = ConnectAnalyticsData["recent_questions"][number];
type ConnectAnalyticsComplaint = ConnectAnalyticsData["customer_complaints"][number];

/**
 * ConnectAnalyticsLive - Live citizen engagement analytics dashboard
 *
 * Feature flag: :connect_analytics_live
 */
export function ConnectAnalyticsLive() {
  const timeframeOptions = [
    { value: "7d", label: "7d" },
    { value: "30d", label: "30d" },
    { value: "quarter", label: "Quarter" },
    { value: "ytd", label: "YTD" },
  ] as const;
  const { municipalities, isLoading: municipalitiesLoading, setMunicipalityId } = useMunicipalityContext();
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<number | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<(typeof timeframeOptions)[number]["value"]>("30d");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<ConnectAnalyticsData | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<ConnectAnalyticsQuestion | null>(null);
  const [selectedComplaint, setSelectedComplaint] = useState<ConnectAnalyticsComplaint | null>(null);
  const [pendingQuestionFromAllQuestions, setPendingQuestionFromAllQuestions] = useState<ConnectAnalyticsQuestion | null>(null);
  const [isQuestionLoading, setIsQuestionLoading] = useState(false);
  const [questionError, setQuestionError] = useState<string | null>(null);
  const [isAllQuestionsOpen, setIsAllQuestionsOpen] = useState(false);
  const [questionsPanelScope, setQuestionsPanelScope] = useState<QuestionsPanelScope>({ kind: "all" });
  const [allQuestions, setAllQuestions] = useState<ConnectAnalyticsQuestion[]>([]);
  const [allQuestionsNextPage, setAllQuestionsNextPage] = useState<number | null>(null);
  const [allQuestionsQuery, setAllQuestionsQuery] = useState("");
  const [isAllQuestionsLoading, setIsAllQuestionsLoading] = useState(false);
  const [isAllQuestionsLoadingMore, setIsAllQuestionsLoadingMore] = useState(false);
  const [allQuestionsError, setAllQuestionsError] = useState<string | null>(null);
  const [allQuestionsSearchTerm, setAllQuestionsSearchTerm] = useState("");
  const [isAllComplaintsOpen, setIsAllComplaintsOpen] = useState(false);
  const [allComplaints, setAllComplaints] = useState<ConnectAnalyticsComplaint[]>([]);
  const [allComplaintsNextPage, setAllComplaintsNextPage] = useState<number | null>(null);
  const [isAllComplaintsLoading, setIsAllComplaintsLoading] = useState(false);
  const [isAllComplaintsLoadingMore, setIsAllComplaintsLoadingMore] = useState(false);
  const [allComplaintsError, setAllComplaintsError] = useState<string | null>(null);
  const [isSourcesSectionExpanded, setIsSourcesSectionExpanded] = useState(false);
  const [expandedSourceCitations, setExpandedSourceCitations] = useState<Record<string, boolean>>({});
  const [analyticsReloadToken, setAnalyticsReloadToken] = useState(0);
  const detailRequestAbortRef = useRef<AbortController | null>(null);
  const detailRequestTokenRef = useRef(0);
  const questionsRequestAbortRef = useRef<AbortController | null>(null);
  const questionsRequestTokenRef = useRef(0);
  const complaintsRequestAbortRef = useRef<AbortController | null>(null);
  const complaintsRequestTokenRef = useRef(0);

  const needsMunicipalitySelection = municipalities.length > 1 && !selectedMunicipalityId;

  useEffect(() => {
    if (municipalities.length === 1 && !selectedMunicipalityId) {
      const onlyMunicipality = municipalities[0];
      setSelectedMunicipalityId(onlyMunicipality.id);
      setMunicipalityId(onlyMunicipality.id);
    }
  }, [municipalities, selectedMunicipalityId, setMunicipalityId]);

  useEffect(() => {
    if (!selectedMunicipalityId) {
      return;
    }

    let isActive = true;
    setIsLoading(true);
    setError(null);

    fetchConnectAnalytics(selectedMunicipalityId, { timeframe: selectedTimeframe })
      .then((data) => {
        if (!isActive) return;
        setAnalytics(data);
      })
      .catch((fetchError: Error) => {
        if (!isActive) return;
        setAnalytics(null);
        setError(formatAnalyticsLoadError(fetchError.message));
      })
      .finally(() => {
        if (!isActive) return;
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [selectedMunicipalityId, selectedTimeframe, analyticsReloadToken]);

  useEffect(() => {
    setAllQuestions([]);
    setAllQuestionsNextPage(null);
    setAllQuestionsQuery("");
    setAllQuestionsError(null);
    setIsAllQuestionsLoading(false);
    setIsAllQuestionsLoadingMore(false);
    setIsAllQuestionsOpen(false);
    setQuestionsPanelScope({ kind: "all" });
    setAllQuestionsSearchTerm("");
    questionsRequestAbortRef.current?.abort();
    questionsRequestAbortRef.current = null;
    questionsRequestTokenRef.current += 1;
  }, [selectedMunicipalityId]);

  useEffect(() => {
    setAllComplaints([]);
    setAllComplaintsNextPage(null);
    setAllComplaintsError(null);
    setIsAllComplaintsLoading(false);
    setIsAllComplaintsLoadingMore(false);
    setIsAllComplaintsOpen(false);
    complaintsRequestAbortRef.current?.abort();
    complaintsRequestAbortRef.current = null;
    complaintsRequestTokenRef.current += 1;
  }, [selectedMunicipalityId, selectedTimeframe]);

  const popularTopics = analytics?.popular_topics ?? [];
  const monthlyQuestions = analytics?.questions_over_time ?? analytics?.questions_per_month ?? [];
  const recentQuestions = analytics?.recent_questions ?? [];
  const citizenInsightsSummary = analytics?.citizen_insights?.summary ?? null;
  const customerComplaints = analytics?.customer_complaints ?? [];
  const executiveSnapshot = analytics?.executive_snapshot;
  const residentInteractions = executiveSnapshot?.resident_interactions;
  const topTopic = executiveSnapshot?.top_topic;
  const interactionVelocity = executiveSnapshot?.daily_interaction_velocity;
  const residentInteractionDelta = residentInteractions?.delta_percentage;
  const trendDirection = residentInteractionDelta == null
    ? "flat"
    : residentInteractionDelta > 0
      ? "up"
      : residentInteractionDelta < 0
        ? "down"
        : "flat";
  const trendLabel = residentInteractionDelta == null
    ? "No previous period baseline"
    : `${residentInteractionDelta > 0 ? "+" : ""}${residentInteractionDelta}% vs previous period`;
  const insightLines = useMemo(() => {
    if (!citizenInsightsSummary) {
      return [];
    }

    return citizenInsightsSummary
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }, [citizenInsightsSummary]);
  const normalizedInsightLines = useMemo(
    () => insightLines.map((line) => line.replace(/^[-*•]\s+/, "")),
    [insightLines],
  );

  const selectedMunicipality = useMemo(() => {
    if (selectedMunicipalityId) {
      return municipalities.find((municipality) => municipality.id === selectedMunicipalityId);
    }
    if (municipalities.length === 1) {
      return municipalities[0];
    }
    return undefined;
  }, [municipalities, selectedMunicipalityId]);
  const canViewConstituentComplaints = selectedMunicipality?.constituent_complaint === true;
  const selectedTimeframeLabel = timeframeOptions.find((option) => option.value === selectedTimeframe)?.label ?? selectedTimeframe;
  const complaintsEmptyMessage = `No complaints in the selected timeframe (${selectedTimeframeLabel}).`;
  const trendInterpretation = useMemo(() => {
    if (!residentInteractions || !topTopic) {
      return "Interaction patterns will appear as more questions arrive.";
    }
    return `${topTopic.name} leads demand with ${topTopic.share_percentage}% of resident interactions in this window.`;
  }, [residentInteractions, topTopic]);
  const TrendIcon = getTrendIcon(trendDirection);
  const parsedSourceCitations = useMemo(() => {
    const rawSources = selectedQuestion?.sources?.length
      ? selectedQuestion.sources
      : (selectedQuestion?.source ? [selectedQuestion.source] : []);

    return rawSources.map((rawSource, index) => {
      const sourceMatch = rawSource.match(/^\s*\{\s*"([^"]+)"\s*=>\s*"([\s\S]*)"\s*\}\s*$/);
      if (!sourceMatch) {
        return {
          id: `source-${index}`,
          citationName: rawSource,
          citationText: rawSource,
        };
      }

      const citationName = sourceMatch[1]?.replace(/\\"/g, "\"").trim();
      const citationText = sourceMatch[2]?.replace(/\\"/g, "\"").replace(/\\n/g, "\n").trim();

      return {
        id: `source-${index}`,
        citationName: citationName || rawSource,
        citationText: citationText || rawSource,
      };
    });
  }, [selectedQuestion?.source, selectedQuestion?.sources]);

  function getTrendIcon(direction: "up" | "down" | "flat") {
    if (direction === "up") return TrendingUp;
    if (direction === "down") return TrendingDown;
    return Minus;
  }

  function formatComplaintAddress(addressData: ConnectAnalyticsComplaint["address_data"]) {
    if (!addressData) {
      return null;
    }

    return addressData.formatted_address?.trim()
      || addressData.address?.trim()
      || null;
  }

  function formatAnalyticsLoadError(message: string | undefined) {
    const normalized = message?.trim();
    if (!normalized || normalized === "An unexpected error occurred") {
      return "We couldn't load analytics right now. Please try again in a moment.";
    }

    return normalized;
  }

  function retryAnalyticsLoad() {
    setAnalyticsReloadToken((current) => current + 1);
  }

  function cancelDetailRequest() {
    detailRequestTokenRef.current += 1;
    detailRequestAbortRef.current?.abort();
    detailRequestAbortRef.current = null;
    setIsQuestionLoading(false);
  }

  function cancelQuestionsRequest() {
    questionsRequestTokenRef.current += 1;
    questionsRequestAbortRef.current?.abort();
    questionsRequestAbortRef.current = null;
    setIsAllQuestionsLoading(false);
    setIsAllQuestionsLoadingMore(false);
  }

  useEffect(() => () => {
    detailRequestAbortRef.current?.abort();
    questionsRequestAbortRef.current?.abort();
    complaintsRequestAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (isAllQuestionsOpen || !pendingQuestionFromAllQuestions) {
      return;
    }

    void openQuestionDetail(pendingQuestionFromAllQuestions);
    setPendingQuestionFromAllQuestions(null);
  }, [isAllQuestionsOpen, pendingQuestionFromAllQuestions]);

  useEffect(() => {
    if (!isAllQuestionsOpen || !selectedMunicipalityId) {
      return;
    }

    void loadAllQuestionsPage({
      append: false,
      query: allQuestionsSearchTerm,
      topic: questionsPanelScope.kind === "topic" ? questionsPanelScope.topic : undefined,
    });
  }, [allQuestionsSearchTerm, isAllQuestionsOpen, questionsPanelScope, selectedMunicipalityId]);

  useEffect(() => {
    if (!isAllComplaintsOpen || !selectedMunicipalityId) {
      return;
    }

    void loadAllComplaintsPage({ append: false });
  }, [isAllComplaintsOpen, selectedMunicipalityId, selectedTimeframe]);

  async function openAllQuestionsPanel() {
    setQuestionsPanelScope({ kind: "all" });
    setIsAllQuestionsOpen(true);
    setAllQuestionsError(null);
  }

  async function openTopicQuestionsPanel(topic: string) {
    setQuestionsPanelScope({ kind: "topic", topic });
    setAllQuestionsSearchTerm("");
    setIsAllQuestionsOpen(true);
    setAllQuestionsError(null);
  }

  async function loadAllQuestionsPage({ append, query, topic }: { append: boolean; query: string; topic?: string }) {
    if (!selectedMunicipalityId || isAllQuestionsLoading || isAllQuestionsLoadingMore) {
      return;
    }
    if (append && !allQuestionsNextPage) {
      return;
    }

    cancelQuestionsRequest();
    const controller = new AbortController();
    questionsRequestAbortRef.current = controller;
    const requestToken = questionsRequestTokenRef.current;
    if (append) {
      setIsAllQuestionsLoadingMore(true);
    } else {
      setIsAllQuestionsLoading(true);
    }

    try {
      const response = await fetchConnectAnalyticsQuestions(selectedMunicipalityId, {
        signal: controller.signal,
        page: append && allQuestionsNextPage ? allQuestionsNextPage : 1,
        q: query,
        topic,
      });
      if (controller.signal.aborted || questionsRequestTokenRef.current !== requestToken) {
        return;
      }

      const pageQuestions = response.questions ?? [];
      const mergedQuestions = append ? [...allQuestions, ...pageQuestions] : pageQuestions;
      setAllQuestions(mergedQuestions);
      setAllQuestionsNextPage(response.next_page);
      setAllQuestionsQuery(query);
    } catch (fetchError) {
      if ((fetchError instanceof DOMException && fetchError.name === "AbortError") || controller.signal.aborted) {
        return;
      }
      if (questionsRequestTokenRef.current !== requestToken) {
        return;
      }
      const message = fetchError instanceof Error ? fetchError.message : "Failed to load questions.";
      setAllQuestionsError(message);
    } finally {
      if (questionsRequestTokenRef.current === requestToken) {
        setIsAllQuestionsLoading(false);
        setIsAllQuestionsLoadingMore(false);
      }
      if (questionsRequestAbortRef.current === controller) {
        questionsRequestAbortRef.current = null;
      }
    }
  }

  function cancelComplaintsRequest() {
    complaintsRequestTokenRef.current += 1;
    complaintsRequestAbortRef.current?.abort();
    complaintsRequestAbortRef.current = null;
    setIsAllComplaintsLoading(false);
    setIsAllComplaintsLoadingMore(false);
  }

  async function openAllComplaintsPanel() {
    setIsAllComplaintsOpen(true);
    setAllComplaintsError(null);
  }

  async function loadAllComplaintsPage({ append }: { append: boolean }) {
    if (!selectedMunicipalityId || isAllComplaintsLoading || isAllComplaintsLoadingMore) {
      return;
    }
    if (append && !allComplaintsNextPage) {
      return;
    }

    cancelComplaintsRequest();
    const controller = new AbortController();
    complaintsRequestAbortRef.current = controller;
    const requestToken = complaintsRequestTokenRef.current;
    if (append) {
      setIsAllComplaintsLoadingMore(true);
    } else {
      setIsAllComplaintsLoading(true);
    }

    try {
      const response = await fetchConnectAnalyticsComplaints(selectedMunicipalityId, {
        signal: controller.signal,
        page: append && allComplaintsNextPage ? allComplaintsNextPage : 1,
        timeframe: selectedTimeframe,
      });
      if (controller.signal.aborted || complaintsRequestTokenRef.current !== requestToken) {
        return;
      }

      const pageComplaints = response.complaints ?? [];
      const mergedComplaints = append ? [...allComplaints, ...pageComplaints] : pageComplaints;
      setAllComplaints(mergedComplaints);
      setAllComplaintsNextPage(response.next_page);
    } catch (fetchError) {
      if ((fetchError instanceof DOMException && fetchError.name === "AbortError") || controller.signal.aborted) {
        return;
      }
      if (complaintsRequestTokenRef.current !== requestToken) {
        return;
      }
      const message = fetchError instanceof Error ? fetchError.message : "Failed to load complaints.";
      setAllComplaintsError(message);
    } finally {
      if (complaintsRequestTokenRef.current === requestToken) {
        setIsAllComplaintsLoading(false);
        setIsAllComplaintsLoadingMore(false);
      }
      if (complaintsRequestAbortRef.current === controller) {
        complaintsRequestAbortRef.current = null;
      }
    }
  }

  async function openQuestionDetail(item: ConnectAnalyticsQuestion) {
    cancelDetailRequest();
    setSelectedQuestion(item);
    setQuestionError(null);
    setIsSourcesSectionExpanded(false);
    setExpandedSourceCitations({});

    if (item.answer || !selectedMunicipalityId) {
      return;
    }

    const controller = new AbortController();
    detailRequestAbortRef.current = controller;
    const requestToken = detailRequestTokenRef.current;
    setIsQuestionLoading(true);
    try {
      const detail = await fetchConnectAnalyticsQuestion(selectedMunicipalityId, item.id, controller.signal);
      if (controller.signal.aborted || detailRequestTokenRef.current !== requestToken) {
        return;
      }

      setSelectedQuestion((current) => {
        if (!current || current.id !== item.id) {
          return current;
        }
        return detail;
      });
      setAnalytics((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          recent_questions: previous.recent_questions.map((question) => (question.id === detail.id ? detail : question)),
        };
      });
    } catch (fetchError) {
      if ((fetchError instanceof DOMException && fetchError.name === "AbortError") || controller.signal.aborted) {
        return;
      }
      if (detailRequestTokenRef.current !== requestToken) {
        return;
      }
      const message = fetchError instanceof Error ? fetchError.message : "Failed to load question details.";
      setQuestionError(message);
    } finally {
      if (detailRequestTokenRef.current === requestToken) {
        setIsQuestionLoading(false);
      }
      if (detailRequestAbortRef.current === controller) {
        detailRequestAbortRef.current = null;
      }
    }
  }

  function openComplaintDetailFromAllComplaints(item: ConnectAnalyticsComplaint) {
    setIsAllComplaintsOpen(false);
    setSelectedComplaint(item);
  }

  function openQuestionDetailFromRecentQuestions(item: ConnectAnalyticsQuestion) {
    void openQuestionDetail(item);
  }

  function openQuestionDetailFromAllQuestions(item: ConnectAnalyticsQuestion) {
    setPendingQuestionFromAllQuestions(item);
    setIsAllQuestionsOpen(false);
  }

  if (isLoading || (municipalitiesLoading && municipalities.length === 0)) {
    return (
      <div className="h-full w-full flex flex-col">
        <div className="p-4 flex-shrink-0">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">Connect Analytics Live</h1>
            <p className="text-muted-foreground">
              Citizen engagement insights from Ordinal Connect
            </p>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center space-x-2" role="status" aria-label="Loading analytics">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-muted-foreground">Loading analytics...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full w-full flex flex-col">
        <div className="p-4 flex-shrink-0">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">Connect Analytics Live</h1>
            <p className="text-muted-foreground">
              Citizen engagement insights from Ordinal Connect
            </p>
          </div>
        </div>
        <div className="flex-1 p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="space-y-3">
              <p>{error}</p>
              <Button type="button" variant="outline" size="sm" onClick={retryAnalyticsLoad}>
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div className="p-4 flex items-start justify-between gap-4 flex-shrink-0">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">Connect Analytics Live</h1>
          <p className="text-muted-foreground">
            Citizen engagement insights from Ordinal Connect
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary">Live Data</Badge>
          <Tabs value={selectedTimeframe} onValueChange={(value) => setSelectedTimeframe(value as (typeof timeframeOptions)[number]["value"])}>
            <TabsList>
              {timeframeOptions.map((option) => (
                <TabsTrigger key={option.value} value={option.value}>
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {municipalities.length > 1 && (
            <div className="w-64">
              <Select
                value={selectedMunicipalityId ? String(selectedMunicipalityId) : ""}
                onValueChange={(value) => {
                  const parsedId = Number(value);
                  setSelectedMunicipalityId(parsedId);
                  setMunicipalityId(parsedId);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a municipality" />
                </SelectTrigger>
                <SelectContent>
                  {municipalities.map((municipality) => (
                    <SelectItem key={municipality.id} value={municipality.id.toString()}>
                      {formatMunicipalityLabel(municipality)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div className="px-4 pb-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Card className="bg-muted/20">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Resident interactions</p>
              <div className="mt-2 flex items-end justify-between gap-3">
                <p className="text-2xl font-semibold">{residentInteractions?.value ?? 0}</p>
                <div className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground">
                  <TrendIcon className="h-3.5 w-3.5" />
                  <span>{trendLabel}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-muted/20">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Top demand topic</p>
              <p className="mt-2 text-lg font-semibold">{topTopic?.name ?? "Not enough data"}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {topTopic ? `${topTopic.count} questions (${topTopic.share_percentage}% share)` : "A top topic will appear once enough interactions are available."}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-muted/20">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Interaction velocity</p>
              <p className="mt-2 text-2xl font-semibold">{interactionVelocity?.value ?? 0}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {interactionVelocity?.unit ?? "questions_per_day"}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="px-4 pb-4">
        <Alert>
          <AlertDescription>{trendInterpretation}</AlertDescription>
        </Alert>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full p-4 pt-0">
          {needsMunicipalitySelection ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              Select a municipality to view analytics.
            </div>
          ) : (
            <div className="grid gap-3 grid-cols-2 h-full" style={{ gridTemplateRows: "1fr 1fr 1fr" }}>
              <Card className="flex flex-col h-full min-h-0">
                <CardHeader className="flex-shrink-0">
                  <CardTitle className="text-lg">Most Popular Topics</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 min-h-0 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Topic</TableHead>
                        <TableHead className="text-right">Chats</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {popularTopics.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} className="text-sm text-muted-foreground">
                            No topics yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        popularTopics.map((item) => (
                          <TableRow
                            key={item.topic}
                            className="cursor-pointer hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            role="button"
                            tabIndex={0}
                            onClick={() => openTopicQuestionsPanel(item.topic)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                void openTopicQuestionsPanel(item.topic);
                              }
                            }}
                          >
                            <TableCell className="font-medium">{item.topic}</TableCell>
                            <TableCell className="text-right font-semibold">{item.chats}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card className="flex flex-col h-full min-h-0">
                <CardHeader className="flex-shrink-0">
                  <CardTitle className="text-lg">Interaction Trend</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 min-h-0">
                  <div className="h-full w-full">
                    <LineChart
                      data={monthlyQuestions}
                      title="Questions Over Time"
                      description="Citizen engagement trend for the selected timeframe"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="flex flex-col h-full min-h-0">
                <CardHeader className="flex-shrink-0 pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-lg">Recent Questions</CardTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={openAllQuestionsPanel}
                      aria-label="View all questions"
                    >
                      View all
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 min-h-0 p-3 overflow-hidden">
                  <div
                    className="h-full max-h-full overflow-y-auto space-y-3 pr-1 ordinal-scrollbar"
                    data-testid="recent-questions-list"
                  >
                    {recentQuestions.length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        No recent questions yet.
                      </div>
                    ) : (
                      recentQuestions.map((item, index) => (
                        <button
                          type="button"
                          key={item.id || index}
                          className="w-full text-left flex items-start space-x-3 p-3 rounded-lg border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:bg-accent/40"
                          onClick={() => openQuestionDetailFromRecentQuestions(item)}
                        >
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 leading-5 line-clamp-2 break-words">
                              {item.content}
                            </p>
                            <div className="flex items-center space-x-2 mt-1">
                              <relative-time datetime={item.asked_at} className="text-xs text-muted-foreground" />
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="flex flex-col h-full min-h-0">
                <CardHeader className="flex-shrink-0 pb-2">
                  <CardTitle className="text-lg">Citizen Insights</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 min-h-0 p-3 overflow-hidden">
                  <div
                    className="h-full max-h-full overflow-y-auto pr-1 ordinal-scrollbar"
                    data-testid="citizen-insights-content"
                  >
                    {citizenInsightsSummary ? (
                      <div className="space-y-3">
                        {normalizedInsightLines.map((line, index) => (
                          <div key={`${line}-${index}`} className="flex items-start space-x-3 p-3 rounded-lg border" data-testid="citizen-insight-item">
                            <div className="flex-shrink-0">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 leading-6 break-words">
                                {line}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        Insights will appear as more questions come in.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {canViewConstituentComplaints && (
                <Card className="flex flex-col h-full min-h-0 col-span-2">
                  <CardHeader className="flex-shrink-0 pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-lg">Constituent Complaints</CardTitle>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={openAllComplaintsPanel}
                        aria-label="View all complaints"
                      >
                        View all
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 min-h-0 p-3 overflow-hidden">
                    <div className="space-y-3 h-full overflow-y-auto ordinal-scrollbar">
                      {customerComplaints.length === 0 ? (
                        <div className="text-sm text-muted-foreground">
                          {complaintsEmptyMessage}
                        </div>
                      ) : (
                        customerComplaints.map((item) => {
                          const addressLabel = formatComplaintAddress(item.address_data);

                          return (
                            <button
                              type="button"
                              key={item.id}
                              className="w-full text-left flex items-start space-x-3 p-3 rounded-lg border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:bg-accent/40"
                              onClick={() => setSelectedComplaint(item)}
                            >
                              <div className="flex-shrink-0">
                                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 leading-5 line-clamp-2 break-words">
                                  {item.complaint}
                                </p>
                                <div className="flex items-center flex-wrap gap-x-2 mt-1 text-xs text-muted-foreground">
                                  <relative-time datetime={item.submitted_at} />
                                  {(item.contact_name || item.contact_email) && <span aria-hidden="true">•</span>}
                                  {item.contact_name && <span>{item.contact_name}</span>}
                                  {item.contact_name && item.contact_email && <span aria-hidden="true">•</span>}
                                  {item.contact_email && <span>{item.contact_email}</span>}
                                </div>
                                {(addressLabel || item.photo_count > 0) && (
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    {addressLabel && (
                                      <Badge variant="outline" className="gap-1 font-normal">
                                        <MapPin className="h-3 w-3" />
                                        <span className="max-w-[220px] truncate">{addressLabel}</span>
                                      </Badge>
                                    )}
                                    {item.photo_count > 0 && (
                                      <Badge variant="secondary" className="gap-1 font-normal">
                                        <ImageIcon className="h-3 w-3" />
                                        {item.photo_count} photo{item.photo_count === 1 ? "" : "s"}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>

      <Sheet
        open={isAllQuestionsOpen}
        onOpenChange={(open) => {
          setIsAllQuestionsOpen(open);
          if (!open) {
            cancelQuestionsRequest();
          }
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <div className="flex h-full min-h-0 flex-col">
            <SheetHeader className="space-y-3 border-b px-6 pb-4 pt-6">
              <div className="space-y-1">
                <SheetTitle>
                  {questionsPanelScope.kind === "topic" ? `${questionsPanelScope.topic} Questions` : "All Questions"}
                </SheetTitle>
                <SheetDescription>
                  {questionsPanelScope.kind === "topic"
                    ? "Sorted by recency within this topic. Click any question to view full answer details."
                    : "Sorted by recency. Click any question to view full answer details."}
                </SheetDescription>
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={allQuestionsSearchTerm}
                  onChange={(event) => setAllQuestionsSearchTerm(event.target.value)}
                  placeholder={questionsPanelScope.kind === "topic" ? "Filter by question or answer" : "Filter by question, answer, or topic"}
                  className="pl-8"
                  aria-label="Filter questions"
                />
              </div>
            </SheetHeader>

            <div
              className="min-h-0 flex-1 overflow-y-auto px-6 py-4 ordinal-scrollbar"
              data-testid="all-questions-list"
            >
              {isAllQuestionsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-label="Loading all questions">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading questions...</span>
                </div>
              ) : allQuestionsError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>Failed to load questions: {allQuestionsError}</AlertDescription>
                </Alert>
              ) : allQuestions.length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  {allQuestionsSearchTerm.trim().length > 0
                    ? (questionsPanelScope.kind === "topic" ? "No questions in this topic match your search." : "No questions match your search.")
                    : (questionsPanelScope.kind === "topic" ? "No questions in this topic yet." : "No questions yet.")}
                </div>
              ) : (
                <div className="space-y-3">
                  {allQuestions.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      className="w-full rounded-lg border p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:bg-accent/40"
                      onClick={() => openQuestionDetailFromAllQuestions(item)}
                    >
                      <p className="text-sm font-medium leading-5 text-gray-900 line-clamp-2 break-words">{item.content}</p>
                      <p className="mt-2 text-xs text-muted-foreground line-clamp-2 break-words">
                        {item.answer ?? "Answer unavailable. Click to load details."}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <relative-time datetime={item.asked_at} />
                        {item.topic && (
                          <>
                            <span aria-hidden="true">•</span>
                            <span>{item.topic}</span>
                          </>
                        )}
                      </div>
                    </button>
                  ))}
                  {allQuestionsNextPage && (
                    <div className="pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isAllQuestionsLoadingMore}
                        onClick={() =>
                          loadAllQuestionsPage({
                            append: true,
                            query: allQuestionsQuery,
                            topic: questionsPanelScope.kind === "topic" ? questionsPanelScope.topic : undefined,
                          })}
                      >
                        {isAllQuestionsLoadingMore ? "Loading more..." : "Load more"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet
        open={isAllComplaintsOpen}
        onOpenChange={(open) => {
          setIsAllComplaintsOpen(open);
          if (!open) {
            cancelComplaintsRequest();
          }
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <div className="flex h-full min-h-0 flex-col">
            <SheetHeader className="space-y-3 border-b px-6 pb-4 pt-6">
              <div className="space-y-1">
                <SheetTitle>All Complaints</SheetTitle>
                <SheetDescription>
                  Complaints submitted in the selected timeframe ({selectedTimeframeLabel}). Click any complaint to view details.
                </SheetDescription>
              </div>
            </SheetHeader>

            <div
              className="min-h-0 flex-1 overflow-y-auto px-6 py-4 ordinal-scrollbar"
              data-testid="all-complaints-list"
            >
              {isAllComplaintsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-label="Loading all complaints">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading complaints...</span>
                </div>
              ) : allComplaintsError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>Failed to load complaints: {allComplaintsError}</AlertDescription>
                </Alert>
              ) : allComplaints.length === 0 ? (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  {complaintsEmptyMessage}
                </div>
              ) : (
                <div className="space-y-3">
                  {allComplaints.map((item) => {
                    const addressLabel = formatComplaintAddress(item.address_data);

                    return (
                      <button
                        type="button"
                        key={item.id}
                        className="w-full rounded-lg border p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:bg-accent/40"
                        onClick={() => openComplaintDetailFromAllComplaints(item)}
                      >
                        <p className="text-sm font-medium leading-5 text-gray-900 line-clamp-2 break-words">{item.complaint}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <relative-time datetime={item.submitted_at} />
                          {item.contact_name && (
                            <>
                              <span aria-hidden="true">•</span>
                              <span>{item.contact_name}</span>
                            </>
                          )}
                        </div>
                        {(addressLabel || item.photo_count > 0) && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {addressLabel && (
                              <Badge variant="outline" className="gap-1 font-normal">
                                <MapPin className="h-3 w-3" />
                                <span className="max-w-[220px] truncate">{addressLabel}</span>
                              </Badge>
                            )}
                            {item.photo_count > 0 && (
                              <Badge variant="secondary" className="gap-1 font-normal">
                                <ImageIcon className="h-3 w-3" />
                                {item.photo_count} photo{item.photo_count === 1 ? "" : "s"}
                              </Badge>
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {allComplaintsNextPage && (
                    <div className="pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isAllComplaintsLoadingMore}
                        onClick={() => loadAllComplaintsPage({ append: true })}
                      >
                        {isAllComplaintsLoadingMore ? "Loading more..." : "Load more"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog
        open={Boolean(selectedQuestion)}
        onOpenChange={(open) => {
          if (!open) {
            cancelDetailRequest();
            setSelectedQuestion(null);
            setQuestionError(null);
            setIsSourcesSectionExpanded(false);
            setExpandedSourceCitations({});
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">Question Detail</DialogTitle>
            <DialogDescription className="sr-only">
              Review the selected recent question, answer, timestamp, and metadata.
            </DialogDescription>
          </DialogHeader>

          {selectedQuestion && (
            <div
              className="max-h-[75vh] overflow-y-auto space-y-4 px-6 pb-6 ordinal-scrollbar"
              data-testid="question-detail-content"
            >
              <section className="space-y-1 rounded-md border bg-muted/20 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Question</p>
                <p className="text-sm leading-6">{selectedQuestion.content}</p>
              </section>

              <section className="space-y-1 rounded-md border bg-muted/20 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Answer</p>
                {isQuestionLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-label="Loading answer">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading answer...</span>
                  </div>
                ) : selectedQuestion.answer ? (
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <SafeMarkdown remarkPlugins={[remarkGfm]}>
                      {selectedQuestion.answer}
                    </SafeMarkdown>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Answer unavailable.</p>
                )}
              </section>

              {questionError && !isQuestionLoading && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>Failed to load answer: {questionError}</AlertDescription>
                </Alert>
              )}

              <section className="space-y-1 rounded-md border bg-muted/20 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Asked</p>
                <relative-time datetime={selectedQuestion.asked_at} className="text-sm text-muted-foreground" />
              </section>

              {selectedQuestion.topic && (
                <section className="space-y-1 rounded-md border bg-muted/20 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Topic</p>
                  <p className="text-sm leading-5">{selectedQuestion.topic}</p>
                </section>
              )}

              {parsedSourceCitations.length > 0 && (
                <section className="space-y-1 rounded-md border bg-muted/20 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sources</p>
                  <button
                    type="button"
                    className="mt-1 rounded-sm text-sm text-blue-700 underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    onClick={() => setIsSourcesSectionExpanded((previous) => !previous)}
                    aria-expanded={isSourcesSectionExpanded}
                    aria-controls="question-sources-list"
                  >
                    {isSourcesSectionExpanded ? "Hide sources" : `Show sources (${parsedSourceCitations.length})`}
                  </button>
                  {isSourcesSectionExpanded && (
                    <div id="question-sources-list" className="mt-2 space-y-2">
                      {parsedSourceCitations.map((citation, index) => {
                        const isExpanded = expandedSourceCitations[citation.id] === true;
                        return (
                          <div key={citation.id} className="rounded-md border bg-background p-2">
                            <button
                              type="button"
                              className="w-full rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              onClick={() =>
                                setExpandedSourceCitations((previous) => ({
                                  ...previous,
                                  [citation.id]: !previous[citation.id],
                                }))}
                              aria-expanded={isExpanded}
                              aria-controls={`source-citation-full-text-${citation.id}`}
                            >
                              <p className="text-sm leading-5 text-blue-700 underline underline-offset-2 break-words">
                                {citation.citationName}
                              </p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {isExpanded ? "Hide full citation" : `View full citation ${index + 1}`}
                              </p>
                            </button>
                            {isExpanded && (
                              <div id={`source-citation-full-text-${citation.id}`} className="mt-2 rounded-md border bg-muted/30 p-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Full Citation</p>
                                <p className="mt-1 text-sm leading-5 whitespace-pre-wrap break-words">{citation.citationText}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedComplaint)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedComplaint(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
          <DialogHeader>
            <DialogTitle className="px-6 pt-6">Complaint Detail</DialogTitle>
            <DialogDescription className="sr-only">
              Review the selected constituent complaint, contact details, location, and photos.
            </DialogDescription>
          </DialogHeader>

          {selectedComplaint && (
            <div
              className="max-h-[75vh] overflow-y-auto space-y-4 px-6 pb-6 ordinal-scrollbar"
              data-testid="complaint-detail-content"
            >
              <section className="space-y-1 rounded-md border bg-muted/20 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Complaint</p>
                <p className="text-sm leading-6 whitespace-pre-wrap break-words">{selectedComplaint.complaint}</p>
              </section>

              <section className="space-y-2 rounded-md border p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Submitted</p>
                <relative-time datetime={selectedComplaint.submitted_at} className="text-sm" />
              </section>

              {(selectedComplaint.contact_name || selectedComplaint.contact_email) && (
                <section className="space-y-2 rounded-md border p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contact</p>
                  <div className="space-y-1 text-sm">
                    {selectedComplaint.contact_name && <p>{selectedComplaint.contact_name}</p>}
                    {selectedComplaint.contact_email && (
                      <p className="text-muted-foreground">{selectedComplaint.contact_email}</p>
                    )}
                  </div>
                </section>
              )}

              {formatComplaintAddress(selectedComplaint.address_data) && (
                <section className="space-y-2 rounded-md border p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Location</p>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <p className="leading-6 break-words">{formatComplaintAddress(selectedComplaint.address_data)}</p>
                  </div>
                </section>
              )}

              {selectedComplaint.photos.length > 0 && (
                <section className="space-y-3 rounded-md border p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Photos ({selectedComplaint.photos.length})
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {selectedComplaint.photos.map((photo) => (
                      <a
                        key={photo.id}
                        href={photo.url}
                        target="_blank"
                        rel="noreferrer"
                        className="group overflow-hidden rounded-lg border bg-muted/20"
                      >
                        <img
                          src={photo.url}
                          alt={photo.filename}
                          className="aspect-[4/3] w-full object-cover transition-transform group-hover:scale-[1.02]"
                          loading="lazy"
                        />
                        <div className="border-t px-3 py-2 text-xs text-muted-foreground truncate">
                          {photo.filename}
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
